import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Check, Loader, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import cuttingPlanService from '../services/cuttingPlanService';
import materialCatalogService from '../services/materialCatalogService';
import projectsService from '../services/projectsService';
import toast from 'react-hot-toast';
import ManualPartBuilder from './ManualPartBuilder';
import CuttingPlanCalculator from './CuttingPlanCalculator';
import CuttingPlanPreview from './CuttingPlanPreview';

const CuttingPlanWizard = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canAccess = hasRole?.(['GERENCIA']) || hasRole?.(['RECEPCAO_COMPRAS_AGENDA']);

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [planData, setPlanData] = useState({
    projectId: '',
    description: '',
    boardWidth: 2.75,
    boardHeight: 1.85,
    kerfWidth: 0.0025,
    notes: ''
  });

  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [parts, setParts] = useState([]);
  const [calculationResult, setCalculationResult] = useState(null);
  const [createdPlan, setCreatedPlan] = useState(null);

  const { data: projectsData, isLoading: loadingProjects } = useQuery({
    queryKey: ['projects-for-cut-plan'],
    queryFn: () => projectsService.getAllProjects({ limit: 100 }),
    enabled: currentStep === 1
  });

  const { data: materialsData, isLoading: loadingMaterials } = useQuery({
    queryKey: ['materials-for-cutting'],
    queryFn: () => materialCatalogService.getAllMaterials({ categoria: 'MDF' }),
    enabled: currentStep === 1
  });

  const projects = projectsData?.data?.data || projectsData?.data?.projects || [];
  const materials = materialsData?.data?.data || materialsData?.data || [];

  useEffect(() => {
    if (!canAccess) {
      toast.error('Você não tem permissão para acessar este recurso');
      navigate('/cutting-plans');
    }
  }, [canAccess, navigate]);

  const handleProjectSelect = (projectId) => {
    setPlanData(prev => ({ ...prev, projectId }));
  };

  const handleMaterialSelect = (material) => {
    setSelectedMaterial(material);
    if (material.largura_chapa && material.comprimento_chapa) {
      setPlanData(prev => ({
        ...prev,
        boardWidth: parseFloat(material.largura_chapa),
        boardHeight: parseFloat(material.comprimento_chapa)
      }));
    }
  };

  const handleDimensionsChange = (field, value) => {
    setPlanData(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  const handlePartsChange = (newParts) => {
    setParts(newParts);
  };

  const handleCalculationResult = (result) => {
    setCalculationResult(result);
  };

  const handleCreatePlan = async () => {
    setIsSubmitting(true);
    try {
      const result = await cuttingPlanService.createPlan(planData);
      if (result.success) {
        setCreatedPlan(result.data);
        toast.success('Plano de corte criado!');
        return result.data;
      } else {
        toast.error(result.message || 'Erro ao criar plano');
        return null;
      }
    } catch (error) {
      console.error('Erro ao criar plano:', error);
      toast.error('Erro ao criar plano de corte');
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      if (parts.length === 0) {
        toast.error('Adicione pelo menos uma peça');
        return;
      }
    }

    if (currentStep === 3) {
      if (!calculationResult) {
        toast.error('Calcule o plano primeiro');
        return;
      }
    }

    setCurrentStep(prev => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleFinish = () => {
    navigate(`/cutting-plans/${createdPlan?.id || planData.id}`);
  };

  const handleGenerate = async () => {
    if (!createdPlan && !planData.id) {
      const newPlan = await handleCreatePlan();
      if (!newPlan) return;
    }

    const planId = createdPlan?.id || planData.id;

    setIsSubmitting(true);
    try {
      const result = await cuttingPlanService.generatePlan(planId, {
        materialId: selectedMaterial?.id,
        boardWidth: planData.boardWidth * 1000,
        boardHeight: planData.boardHeight * 1000,
        boardThickness: selectedMaterial?.espessura_mm || 18,
        parts: parts.map(p => ({
          partCode: p.partCode || p.part_code,
          partName: p.partName || p.name,
          width: parseInt(p.width),
          height: parseInt(p.height),
          thickness: parseInt(p.thickness) || 18,
          quantity: parseInt(p.quantity) || 1,
          materialName: p.materialName || p.material || selectedMaterial?.nome || 'MDF'
        }))
      });

      if (result.success) {
        toast.success('Plano gerado com sucesso!');
        setCurrentStep(4);
      } else {
        toast.error(result.message || 'Erro ao gerar plano');
      }
    } catch (error) {
      console.error('Erro ao gerar plano:', error);
      toast.error('Erro ao gerar plano de corte');
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { id: 1, name: 'Projeto e Material', description: 'Selecione o projeto e material' },
    { id: 2, name: 'Peças', description: 'Adicione as peças do plano' },
    { id: 3, name: 'Cálculo', description: 'Calcule o aproveitamento' },
    { id: 4, name: 'Gerar', description: 'Gere as chapas e posições' }
  ];

  if (!canAccess) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
          <p className="text-red-800 font-medium">Acesso negado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/cutting-plans')}
          className="text-blue-600 hover:text-blue-800 text-sm inline-flex items-center gap-1"
        >
          <ChevronLeft className="w-4 h-4" />
          Voltar para lista de planos
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          Criar Plano de Corte - Wizard
        </h1>

        <nav aria-label="Progress">
          <ol className="flex items-center">
            {steps.map((step, stepIdx) => (
              <li
                key={step.id}
                className={`relative ${stepIdx !== steps.length - 1 ? 'pr-8 sm:pr-20' : ''}`}
              >
                {step.id < currentStep ? (
                  <div className="flex items-center">
                    <button
                      onClick={() => setCurrentStep(step.id)}
                      className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
                    >
                      <Check className="w-5 h-5 text-white" />
                    </button>
                    <div className="hidden sm:block ml-3">
                      <p className="text-sm font-medium text-gray-900">{step.name}</p>
                      <p className="text-sm text-gray-500">{step.description}</p>
                    </div>
                  </div>
                ) : step.id === currentStep ? (
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-medium">{step.id}</span>
                    </div>
                    <div className="hidden sm:block ml-3">
                      <p className="text-sm font-medium text-blue-600">{step.name}</p>
                      <p className="text-sm text-gray-500">{step.description}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                      <span className="text-gray-500 text-sm font-medium">{step.id}</span>
                    </div>
                    <div className="hidden sm:block ml-3">
                      <p className="text-sm font-medium text-gray-500">{step.name}</p>
                      <p className="text-sm text-gray-400">{step.description}</p>
                    </div>
                  </div>
                )}

                {stepIdx !== steps.length - 1 && (
                  <div
                    className={`hidden sm:block absolute top-4 -right-4 h-0.5 w-full ${
                      step.id < currentStep ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </li>
            ))}
          </ol>
        </nav>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {currentStep === 1 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Step 1: Projeto e Material</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Projeto *
              </label>
              <select
                value={planData.projectId}
                onChange={(e) => handleProjectSelect(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                disabled={loadingProjects}
              >
                <option value="">Selecione um projeto</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.project_code} - {project.client_name || project.client_id}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Material Padrão
              </label>
              <select
                value={selectedMaterial?.id || ''}
                onChange={(e) => {
                  const mat = materials.find(m => m.id === parseInt(e.target.value));
                  handleMaterialSelect(mat);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                disabled={loadingMaterials}
              >
                <option value="">Selecione um material (opcional)</option>
                {materials.map((material) => (
                  <option key={material.id} value={material.id}>
                    {material.nome} - {material.largura_chapa}x{material.comprimento_chapa}x{material.espessura_mm}mm
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Largura da Chapa (m)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={planData.boardWidth}
                  onChange={(e) => handleDimensionsChange('boardWidth', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Altura da Chapa (m)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={planData.boardHeight}
                  onChange={(e) => handleDimensionsChange('boardHeight', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kerf (mm)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={planData.kerfWidth * 1000}
                  onChange={(e) => handleDimensionsChange('kerfWidth', parseFloat(e.target.value) / 1000)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrição do Plano
              </label>
              <input
                type="text"
                value={planData.description}
                onChange={(e) => setPlanData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: Penteadeira Luciana"
              />
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Step 2: Adicionar Peças</h2>
            <ManualPartBuilder
              parts={parts}
              onPartsChange={handlePartsChange}
              selectedMaterial={selectedMaterial}
            />
          </div>
        )}

        {currentStep === 3 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Step 3: Calcular</h2>
            <CuttingPlanCalculator
              parts={parts}
              boardWidth={planData.boardWidth}
              boardHeight={planData.boardHeight}
              kerf={planData.kerfWidth}
              onCalculationResult={handleCalculationResult}
              onGenerate={handleGenerate}
              isGenerating={isSubmitting}
            />
          </div>
        )}

        {currentStep === 4 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Step 4: Resultado</h2>
            <CuttingPlanPreview
              calculationResult={calculationResult}
              planData={planData}
              onFinish={handleFinish}
            />
          </div>
        )}
      </div>

      <div className="flex justify-between mt-6">
        <button
          onClick={handleBack}
          disabled={currentStep === 1}
          className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          Anterior
        </button>

        {currentStep < 4 && (
          <button
            onClick={handleNext}
            disabled={currentStep === 3 && !calculationResult}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            Próximo
            <ChevronRight className="w-4 h-4" />
          </button>
        )}

        {currentStep === 3 && calculationResult && (
          <button
            onClick={handleGenerate}
            disabled={isSubmitting}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Gerando...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Gerar Plano
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default CuttingPlanWizard;
